import { useCallback, useEffect, useState } from 'react';
import { QrCode, Copy, X, Share2 } from 'lucide-react';
import QRCode from 'qrcode';
import { buildInviteRegisterUrl } from '../../utils/inviteRegisterUrl';
import { buildWaMeUrl } from '../../utils/whatsappSupport';
import { emitToast } from '../../context/toast';
import { copyTextToClipboard } from '../../utils/safeDom';

function copyWithFallback(text) {
  return copyTextToClipboard(text);
}

export default function InviteQrShare({ code, label = 'Código de invitación', onClose }) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const registerUrl = buildInviteRegisterUrl(code);
  const trimmedCode = code?.trim() || '';

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!registerUrl) return;
    QRCode.toDataURL(registerUrl, { width: 240, margin: 2 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [registerUrl]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [handleClose]);

  const copyText = async (text, title, message) => {
    try {
      await copyWithFallback(text);
      emitToast({ type: 'success', title, message: message || 'Copiado al portapapeles' });
      return true;
    } catch {
      emitToast({ type: 'error', title: 'Error', message: 'No se pudo copiar' });
      return false;
    }
  };

  const shareInvite = async () => {
    const shareText = `Únete con el código ${trimmedCode}`;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: label,
          text: shareText,
          url: registerUrl,
        });
        emitToast({ type: 'success', title: 'Compartido', message: 'Enlace enviado correctamente' });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }

    const waMsg = `${shareText}\n${registerUrl}`;
    const waUrl = buildWaMeUrl('573192635819', waMsg);
    const opened = window.open(waUrl, '_blank', 'noopener,noreferrer');
    if (opened) {
      emitToast({ type: 'success', title: 'WhatsApp', message: 'Se abrió WhatsApp para compartir el enlace' });
      return;
    }

    await copyText(registerUrl, 'Enlace copiado', 'Pégalo en WhatsApp, correo o donde quieras compartirlo');
  };

  if (!trimmedCode) return null;

  return (
    <div
      className="form-modal-overlay"
      style={{ zIndex: 9999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-qr-title"
      onClick={handleClose}
    >
      {/* Isla clara: form-modal sin esto hereda shell oscuro → texto ilegible */}
      <div
        className="form-modal d28d-admin-modal ui-light-panel surface-readable bg-white border border-slate-200 w-full max-w-sm rounded-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-modal-header border-b border-slate-200">
          <div className="flex items-center gap-2 min-w-0">
            <QrCode className="text-slate-700 shrink-0" size={22} aria-hidden />
            <h3 id="invite-qr-title" className="font-bold text-slate-900 truncate">{label}</h3>
          </div>
          <button
            type="button"
            className="form-close-btn text-slate-600 hover:text-slate-900"
            onClick={handleClose}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="form-modal-content space-y-4 text-slate-800">
          <p className="text-sm text-slate-600">
            Escanea o comparte el enlace para que nuevos usuarios se registren con este código.
          </p>
          <code className="block text-center font-mono text-sm text-slate-900 bg-slate-100 border border-slate-200 rounded-lg py-2 px-3">
            {trimmedCode}
          </code>
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt={`QR ${trimmedCode}`}
              className="mx-auto rounded-lg border border-slate-200 bg-white p-2"
              width={240}
              height={240}
            />
          ) : (
            <div className="h-[240px] flex items-center justify-center text-slate-500 text-sm">
              Generando QR…
            </div>
          )}
          <p className="text-xs text-slate-600 break-all">{registerUrl}</p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="btn btn-primary w-full inline-flex items-center justify-center gap-2"
              onClick={() => copyText(trimmedCode, 'Código copiado')}
            >
              <Copy size={16} /> Copiar código
            </button>
            <button
              type="button"
              className="btn btn-secondary w-full inline-flex items-center justify-center gap-2"
              onClick={() => copyText(registerUrl, 'Enlace copiado')}
            >
              <Copy size={16} /> Copiar enlace de registro
            </button>
            <button
              type="button"
              className="btn btn-secondary w-full inline-flex items-center justify-center gap-2"
              onClick={shareInvite}
            >
              <Share2 size={16} /> Compartir
            </button>
            <button type="button" className="btn btn-secondary w-full" onClick={handleClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
